import { Progress, Tag } from 'antd';
import React, { useMemo } from 'react';

import Text from '@/atoms/Text';
import { UserAllocationTimelineChart } from '@/components/project_details_components/UserAllocationTimelineChart';
import { UserUtilizationChart } from '@/components/project_details_components/UserUtilizationChart';
import { buildAllocationTimelineData, buildUtilizationChartData } from '@/models/teamCharts.model';

export interface AllocationPeriod {
  allocationPercent: number;
  allocationHours: number;
  reportHours: number;
  allocationStartDate: string;
  allocationEndDate: string;
}

export interface PersonHours {
  name: string;
  role: string;
  allocation: number;
  hours: number;
  totalAllocatedHours?: number;
  allocationDetails?: AllocationPeriod[]; // Array of allocation periods
  isActive?: number; // 1 = active, 0 = inactive
  lastWorkedDate?: string | null;
}

export interface HoursCardProps {
  budgetedHours: number; // Allocated hours budget
  utilizedHours: number; // Actual hours worked
  utilization: number; // Percentage utilization
  people: PersonHours[];
  budgetAmount?: string; // Contract value (e.g., "$65,000")
  obeBudget?: string;
  obeBudgetSource?: string;
  obeMargin?: string;
  obeMarginSource?: string;
  currentCost?: string;
  currentCostStatus?: string;
  currentMargin?: string;
  currentMarginStatus?: string;
}

export function HoursCard({
  budgetedHours,
  utilizedHours,
  utilization,
  people,
  budgetAmount,
  obeBudget,
  obeBudgetSource,
  obeMargin,
  obeMarginSource,
}: Readonly<HoursCardProps>): React.ReactElement {
  const hoursRemaining = budgetedHours - utilizedHours;
  const utilizationData = useMemo(() => buildUtilizationChartData(people), [people]);
  const timelineData = useMemo(() => buildAllocationTimelineData(people), [people]);

  return (
    <div className="hours-card">
      {/* Header */}
      <div className="hours-card__header">
        <Text variant="h5">Hours</Text>
        <div className="hours-card__header-info">
          <Tag className="card__source-tag">Source: OpenAir</Tag>
          <Text
            variant="bodyDefault"
            color="var(--text-secondary)"
            className="hours-card__header-text"
          >
            Budgeted (Pricing) — not yet available
          </Text>
        </div>
      </div>

      {/* UTILIZED section in a bordered box */}
      <div className="hours-card__utilized">
        <div className="hours-card__utilized-header">
          <Text variant="h5">Utilized (OpenAir)</Text>
          {budgetAmount && (
            <Text variant="bodySmall" color="var(--text-secondary)">
              Contract Budget: {budgetAmount}
            </Text>
          )}
        </div>

        <div className="hours-card__utilized-progress">
          <div className="hours-card__utilized-info">
            <Text variant="bodyDefault" className="hours-card__utilized-total">
              {utilizedHours}
              <span className="hours-card__utilized-hour-unit">h / {budgetedHours}h allocated</span>
            </Text>
            <Text variant="bodyDefault" className="hours-card__utilized-percentage">
              {utilization}%
            </Text>
          </div>
          <Progress
            percent={Math.min(100, utilization)}
            strokeColor={
              utilization > 100 || utilizedHours > budgetedHours
                ? '#EF4444'
                : 'var(--color-accent-500)'
            }
          />
        </div>

        <div className="hours-card__utilized-scale">
          <span>0h</span>
          <span>{hoursRemaining}h remaining</span>
          <span>{budgetedHours}h</span>
        </div>
      </div>

      <UserAllocationTimelineChart data={timelineData} />
      <UserUtilizationChart data={utilizationData} />

      {/* OBE Metrics - 4 columns */}
      <div className="hours-card__metrics">
        {obeBudget && (
          <div className="hours-card__metric">
            <Text
              variant="bodySmall"
              color="var(--text-secondary)"
              className="hours-card__metric-label"
            >
              ODE BUDGET
            </Text>
            <Text variant="bodyDefault" className="hours-card__metric-value">
              {obeBudget}
            </Text>
            <Text
              variant="bodySmall"
              color="var(--text-secondary)"
              className="hours-card__metric-source"
            >
              {obeBudgetSource}
            </Text>
          </div>
        )}
        {obeMargin && (
          <div className="hours-card__metric">
            <Text
              variant="bodySmall"
              color="var(--text-secondary)"
              className="hours-card__metric-label"
            >
              ODE MARGIN
            </Text>
            <Text variant="bodyDefault" className="hours-card__metric-value">
              {obeMargin}
            </Text>
            <Text
              variant="bodySmall"
              color="var(--text-secondary)"
              className="hours-card__metric-source"
            >
              {obeMarginSource}
            </Text>
          </div>
        )}
        <div className="hours-card__metric">
          <Text
            variant="bodySmall"
            color="var(--text-secondary)"
            className="hours-card__metric-label"
          >
            CURRENT COST
          </Text>
          <div className="hours-card__metric-coming-soon">
            <input type="checkbox" disabled />
            <Text variant="bodySmall" color="var(--text-secondary)">
              Coming in V2
            </Text>
          </div>
        </div>
        <div className="hours-card__metric">
          <Text
            variant="bodySmall"
            color="var(--text-secondary)"
            className="hours-card__metric-label"
          >
            CURRENT MARGIN
          </Text>
          <div className="hours-card__metric-coming-soon">
            <input type="checkbox" disabled />
            <Text variant="bodySmall" color="var(--text-secondary)">
              Coming in V2
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
